import { useState, useEffect } from "react"
import {
  Button,
  HStack,
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverPositioner,
  VStack,
  Text,
  Separator,
  Box,
} from "@chakra-ui/react"
import { Checkbox } from "@hydrologiq/shared/chakra/Checkbox"
import { FaFilter, FaCheck } from "react-icons/fa6"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import * as gqlExperienceTypes from "@hydrologiq/gql-experience/graphql"

export interface AssetSelectorProps {
  /** Customer ID to fetch assets for */
  customerId: string
  /** Currently selected asset IDs */
  selectedAssetIds: string[]
  /** Callback when asset selection changes */
  onSelectionChange: (assetIds: string[]) => void
}

/**
 * Asset selector component for customer reporting
 * Allows users to select which assets to include in the report
 */
export function AssetSelector({ customerId, selectedAssetIds, onSelectionChange }: AssetSelectorProps) {
  const [open, setOpen] = useState(false)
  const [tempSelection, setTempSelection] = useState<string[]>(selectedAssetIds)

  // Fetch deployed assets for this customer
  const { loading, data, error } = useQueryWithEndpoint(gqlExperienceTypes.GetAssetsDocument, {
    variables: {
      deployedCustomer: customerId,
      perPage: 100, // Get more assets to avoid pagination for now
    },
    skip: !customerId,
  }, "primary")

  const assets = data?.assets?.data || []

  // Sync temp selection with prop changes
  useEffect(() => {
    setTempSelection(selectedAssetIds)
  }, [selectedAssetIds])

  // Initialise with all assets selected when assets are first loaded
  useEffect(() => {
    if (assets.length > 0 && selectedAssetIds.length === 0) {
      const allAssetIds = assets.map((asset: any) => asset.id)
      setTempSelection(allAssetIds)
      onSelectionChange(allAssetIds)
    }
  }, [assets, selectedAssetIds.length, onSelectionChange])

  const handleToggleAsset = (assetId: string) => {
    setTempSelection((prev) => {
      if (prev.includes(assetId)) {
        return prev.filter((id) => id !== assetId)
      } else {
        return [...prev, assetId]
      }
    })
  }

  const handleSelectAll = () => {
    const allAssetIds = assets.map((asset: any) => asset.id)
    setTempSelection(allAssetIds)
  }

  const handleDeselectAll = () => {
    setTempSelection([])
  }

  const handleReset = () => {
    const allAssetIds = assets.map((asset: any) => asset.id)
    setTempSelection(allAssetIds)
  }

  const handleApply = () => {
    onSelectionChange(tempSelection)
    setOpen(false)
  }

  const handleCancel = () => {
    setTempSelection(selectedAssetIds)
    setOpen(false)
  }

  const selectedCount = selectedAssetIds.length
  const totalCount = assets.length

  // Check if at least one asset is selected
  const hasSelectedAssets = tempSelection.length > 0

  if (loading) {
    return (
      <Box>
        <Text fontSize="sm" color="gray.600">
          Loading assets...
        </Text>
      </Box>
    )
  }

  if (error || assets.length === 0) {
    return (
      <Box>
        <Text fontSize="sm" color="gray.600">
          No deployed assets found for this customer.
        </Text>
      </Box>
    )
  }

  return (
    <PopoverRoot open={open} onOpenChange={(e) => setOpen(e.open)} positioning={{ placement: "bottom-start" }}>
      <PopoverTrigger>
        <Button variant={"hydrologiq_secondary" as any} size="sm">
          <HStack gap={2}>
            <FaFilter />
            <Text>
              Assets ({selectedCount}/{totalCount})
            </Text>
          </HStack>
        </Button>
      </PopoverTrigger>

      <PopoverPositioner>
        <PopoverContent width="400px">
          <PopoverBody>
            <VStack align="stretch" gap={3}>
              {/* Header with actions */}
              <HStack justifyContent="space-between">
                <Text fontWeight="semibold">Select assets</Text>
                <HStack gap={1}>
                  <Button size="xs" variant={"hydrologiq_primary" as any} onClick={handleSelectAll}>
                    All
                  </Button>
                  <Button size="xs" variant={"hydrologiq_primary" as any} onClick={handleDeselectAll}>
                    None
                  </Button>
                  <Button size="xs" variant={"hydrologiq_primary" as any} onClick={handleReset}>
                    Reset
                  </Button>
                </HStack>
              </HStack>

              <Separator />

              {/* Asset list with checkboxes */}
              <VStack align="stretch" gap={2} maxHeight="300px" overflowY="auto">
                {assets.map((asset: any) => {
                  const isSelected = tempSelection.includes(asset.id)
                  return (
                    <Checkbox
                      key={asset.id}
                      checked={isSelected}
                      onCheckedChange={() => handleToggleAsset(asset.id)}
                      alignItems="flex-start"
                    >
                      <VStack align="stretch" gap={1}>
                        <Text fontSize="sm" fontWeight="medium">
                          {asset.name}
                        </Text>
                        {asset.current_order?.site && (
                          <Text fontSize="xs" color="gray.600">
                            {asset.current_order.site.name}
                          </Text>
                        )}
                      </VStack>
                    </Checkbox>
                  )
                })}
              </VStack>

              <Separator />

              {/* Footer with Apply/Cancel */}
              <HStack justifyContent="flex-end" gap={2}>
                <Button size="sm" variant={"hydrologiq_secondary" as any} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button size="sm" variant={"hydrologiq_primary" as any} onClick={handleApply} disabled={!hasSelectedAssets}>
                  <HStack gap={1}>
                    <FaCheck />
                    <Text>Apply</Text>
                  </HStack>
                </Button>
              </HStack>
            </VStack>
          </PopoverBody>
        </PopoverContent>
      </PopoverPositioner>
    </PopoverRoot>
  )
}

export default AssetSelector